import {
  BiddingIntradayOrderBulkDeleteFailureReason,
  type DeleteBiddingIntradayOrdersResponse,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_service_pb'

import type { IntradayBidRow } from '~/pages/IntradayBidding/models/intradayBidRow'
import { composeIntradayTableStatus } from '~/pages/IntradayBidding/models/intradayTableStatus'

/**
 * 入札を全削除 (AC-BIN-138) で消せる札 = 一覧のステータスが下書きの札。 承認待ち・差戻し・予約中・
 * 送信中・入札中などは削除しない (下書きの 1 件ずつの削除 AC-BIN-035 と同じ)。
 */
export const isIntradayBulkDeletableBid = (bid: IntradayBidRow): boolean =>
  composeIntradayTableStatus(bid) === 'draft'

/**
 * 「入札を全削除」 を選べない理由のうち、 一覧の札で決まるもの (AC-BIN-138 の表)。 選べる時は
 * undefined。 読み取り専用の理由と一覧の取得前 (理由を出さない) はこれより優先し、 呼び出し側が持つ。
 *
 * - `noBid`: 受渡日の入札が 0 件
 * - `noDraft`: 入札はあるが下書きが 1 件も無い
 * - `hiddenByFilter`: 下書きはあるが、 絞り込みで 1 件も表示されていない
 */
export type IntradayBulkDeleteBlockedCause =
  | 'noBid'
  | 'noDraft'
  | 'hiddenByFilter'

export function intradayBulkDeleteBlockedCause(input: {
  /** 選択中の受渡日の入札の件数 (絞り込む前)。 */
  bidCount: number
  /** 選択中の受渡日の下書きの件数 (絞り込む前)。 */
  draftCount: number
  /** 一覧に表示中の下書きの件数。 */
  listedDraftCount: number
}): IntradayBulkDeleteBlockedCause | undefined {
  if (input.listedDraftCount > 0) return undefined
  if (input.bidCount === 0) return 'noBid'
  return input.draftCount === 0 ? 'noDraft' : 'hiddenByFilter'
}

/**
 * 削除しなかった理由 (バナーの本文の件数と「詳細を見る」 の札ごとの理由)。
 * - `updatedByOther`: 画面に読み込んだ後に更新されていた札 (照合 AC-BIN-123)。 提出・削除・失効・
 *   編集で下書きでなくなった、 または内容が変わった札と、 見つからない札 (削除済み) を含む
 * - `other`: それ以外 (下書きでない状態・GC を過ぎた 等)
 */
export type IntradayBulkDeleteRefusal = 'updatedByOther' | 'other'

const REFUSAL_OF: Record<
  BiddingIntradayOrderBulkDeleteFailureReason,
  IntradayBulkDeleteRefusal
> = {
  [BiddingIntradayOrderBulkDeleteFailureReason.UPDATED_BY_OTHER]:
    'updatedByOther',
  // 削除済みの札は、 画面に読み込んだ後に削除された札として照合で外した側に入れる。
  [BiddingIntradayOrderBulkDeleteFailureReason.NOT_FOUND]: 'updatedByOther',
  [BiddingIntradayOrderBulkDeleteFailureReason.NOT_DELETABLE]: 'other',
  [BiddingIntradayOrderBulkDeleteFailureReason.GATE_CLOSED]: 'other',
  [BiddingIntradayOrderBulkDeleteFailureReason.UNSPECIFIED]: 'other',
}

export type IntradayBulkDeleteRefusedBid = {
  bid: IntradayBidRow
  reason: IntradayBulkDeleteRefusal
}

export type IntradayBulkDeleteOutcome = {
  /** 削除できた札の件数 (バナーの見出しの {D})。 */
  deletedCount: number
  /** 削除しなかった札。 送った対象の並びのまま。 全部削除できた時は空。 */
  refused: IntradayBulkDeleteRefusedBid[]
}

/**
 * 応答の結果 (送った対象と同じ順・同じ件数) を、 削除できた札と削除しなかった札に分ける。
 * 結果の無い札は削除できたと確認できないので、 削除しなかった側 (`other`) に入れる。
 */
export function readIntradayBulkDeleteResponse(
  response: Pick<DeleteBiddingIntradayOrdersResponse, 'results'>,
  targets: readonly IntradayBidRow[],
): IntradayBulkDeleteOutcome {
  const resultById = new Map(response.results.map((r) => [r.id, r.outcome]))
  let deletedCount = 0
  const refused: IntradayBulkDeleteRefusedBid[] = []
  for (const bid of targets) {
    const outcome = resultById.get(bid.id)
    if (outcome?.case === 'deleted') {
      deletedCount += 1
      continue
    }
    refused.push({
      bid,
      reason:
        REFUSAL_OF[
          outcome?.case === 'failure'
            ? outcome.value.reason
            : BiddingIntradayOrderBulkDeleteFailureReason.UNSPECIFIED
        ],
    })
  }
  return { deletedCount, refused }
}

/** バナーの本文の件数 (照合で外した {M}・それ以外 {K})。 0 件の理由は出さない側が判定する。 */
export function intradayBulkDeleteRefusalCounts(
  refused: ReadonlyArray<{ reason: IntradayBulkDeleteRefusal }>,
): Record<IntradayBulkDeleteRefusal, number> {
  return {
    updatedByOther: refused.filter((r) => r.reason === 'updatedByOther').length,
    other: refused.filter((r) => r.reason === 'other').length,
  }
}
